import React from "react"
import { useQuery } from "react-query"
import { Modal } from "@capioweb/ui-components/src"
import { PIWIK_PRO_CONTAINER_URL, PUBLIC_API_ENDPOINT } from "../constants"
import { getContent } from "../api/content"
import { LoginPopUpModelProps, SiteSettingsProps } from "../components/Core"
import {
	EpiContextProvider,
	UIContextProvider,
	LocalesContextProvider,
	AlertsContextProvider,
} from "."
import {
	sendUserLogonDataLayerPush,
	sendUserLogOutDataLayerPush,
} from "../utils/piwikPro"
import {
	isLocalStorageAvailable,
	isNavigatorAvailable,
	isSessionStorageAvailable,
} from "../utils"
import PiwikPro from "@piwikpro/react-piwik-pro"
import { queryStaleTime, queryRefetchInterval } from "../constants"

export interface AppContextProviderProps {
	isSSRRequest?: boolean
	children: React.ReactNode
}

interface AppContextProps {
	isLoggedIn: boolean | null
	userId: string | null
	loginPopUpModel: LoginPopUpModelProps | null
	cookiebotId: string | null
	pageId: string
	setPageId: (pageId: string) => void
	userLocation: GeolocationPosition | null
	getUserLocation: () => void
	setUserLocation: React.Dispatch<React.SetStateAction<GeolocationPosition>>
}

export const AppContext = React.createContext<AppContextProps>({
	isLoggedIn: null,
	userId: null,
	loginPopUpModel: null,
	cookiebotId: null,
	pageId: null,
	userLocation: null,
	setPageId: () => { },
	getUserLocation: () => { },
	setUserLocation: () => { },
})

export const AppContextProvider: React.FC<AppContextProviderProps> = (
	props
) => {
	const [pageId, setPageId] = React.useState<string | null>(null)
	const [userLocation, setUserLocation] =
		React.useState<GeolocationPosition>(null)

	// Site settings query
	const { data: siteSettingsData } = useQuery<SiteSettingsProps>(
		"site",
		() => getContent(`${PUBLIC_API_ENDPOINT}/settings/site/`),
		{
			staleTime: queryStaleTime,
			refetchInterval: queryRefetchInterval,
		}
	)

	trackUserLogon(siteSettingsData?.userId)

	React.useEffect(() => {
		if (siteSettingsData?.piwikID) {
			let hasInitiatedPiwik = false
			let scripts = document.getElementsByTagName("script")
			for (let i = 0; i < scripts.length; i++) {
				if (
					scripts[i].innerText.indexOf(
						`(window, document, 'dataLayer', '${siteSettingsData.piwikID}')`
					) > -1
				) {
					hasInitiatedPiwik = true
				}
			}

			!hasInitiatedPiwik &&
				PiwikPro.initialize(siteSettingsData.piwikID, PIWIK_PRO_CONTAINER_URL)
		}
	}, [siteSettingsData])

	React.useEffect(() => {
		checkIfRemoveLocalStorageItem()
	}, [])

	const getUserLocation = () => {
		if (isNavigatorAvailable() && navigator.geolocation) {
			navigator.geolocation.getCurrentPosition((position) => {
				setUserLocation(position)
			})
		} else {
			return null
		}
	}

	return (
		<AppContext.Provider
			value={{
				isLoggedIn: siteSettingsData?.userId != null,
				userId: siteSettingsData?.userId,
				loginPopUpModel: siteSettingsData?.loginPopUpModel ?? null,
				cookiebotId: null,
				pageId,
				userLocation: userLocation,
				setPageId,
				getUserLocation,
				setUserLocation
			}}
		>
			<AlertsContextProvider>
				<EpiContextProvider ssrRequest={props.isSSRRequest}>
					<UIContextProvider>
						<LocalesContextProvider
							localeSlug={siteSettingsData?.language ?? "sv"}
						>
							<Modal.Provider>{props.children}</Modal.Provider>
						</LocalesContextProvider>
					</UIContextProvider>
				</EpiContextProvider>
			</AlertsContextProvider>
		</AppContext.Provider>
	)
}

const trackUserLogon = (userId?: string) => {
	if (isSessionStorageAvailable()) {
		const storedUserId = sessionStorage.getItem("userId")

		if (!userId && !!storedUserId) {
			// Remove item from session storage if userId is null - this means the user has been logged out
			sendUserLogOutDataLayerPush()
			sessionStorage.removeItem("userId")
		} else if (userId && storedUserId != userId) {
			sendUserLogonDataLayerPush(userId)
			sessionStorage.setItem("userId", userId)
		}
	}
}

const checkIfRemoveLocalStorageItem = () => {
	if (isLocalStorageAvailable()) {
		const currentTime = new Date().getTime()
		const timeCreated = Number(localStorage.getItem("hideLoginPopup"))
		const sevenDays = 6.048e8 //one week in milliseconds
		//removes the item if it was created more than then one week ago
		timeCreated + sevenDays < currentTime &&
			localStorage.removeItem("hideLoginPopup")
	}
}
